import { ComponentType } from '@angular/cdk/overlay';
import { Service, TemplateRef, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import { Subscription, concatMap, delay, filter, firstValueFrom, from, take, takeUntil, tap } from 'rxjs';

import { outputToObservable } from '@angular/core/rxjs-interop';
import { ChatDialogData, ColorVariant } from '@core/models';
import { Keyword } from '../localization';
import { Error } from '../validation';
import { DialogEntryDirection, ExtendedMatDialogConfig } from './dialog-animations';
import { DialogBase } from './dialog-base';

export interface ConfirmationDialogMessageRow {
    label: string;
    value: string;
}

export interface ConfirmationDialogConfig {
    primaryMessageKeyword?: Keyword;
    primaryMessageTemplate?: TemplateRef<HTMLElement>;
    headerMessageKeyword?: Keyword;
    additionalMessage?: Keyword;
    primaryButtonKeyword?: Keyword;
    secondaryMessageKeyword?: Keyword;
    dialogDynamicTranslations?: Record<string, unknown>;
    primaryMessageRows?: ConfirmationDialogMessageRow[];
    textAreaHeaderKeyword?: Keyword;
    dialogTextData?: string;
    showCancelButton?: boolean;
    cancelButtonMessage?: Keyword;
    showAdditionalActionButton?: boolean;
    additionalActionButtonKeyword?: Keyword;
    mainModalColor?: ColorVariant;
    confirmTypePhraseKeyword?: Keyword;
    icon?: string;
}

const defaultConfig: ConfirmationDialogConfig = {
    headerMessageKeyword: 'str_common_confirmation',
    primaryMessageKeyword: null,
    primaryMessageTemplate: null,
    additionalMessage: null,
    secondaryMessageKeyword: null,
    primaryButtonKeyword: 'str_common_confirm',
    dialogDynamicTranslations: {},
    primaryMessageRows: [],
    textAreaHeaderKeyword: null,
    dialogTextData: '',
    showCancelButton: true,
    cancelButtonMessage: 'str_common_goBack',
    showAdditionalActionButton: false,
    additionalActionButtonKeyword: 'str_common_cancel',
};

@Service()
export class DialogService {
    private readonly matDialogService = inject(MatDialog);

    /**
     * Opens a right-side menu dialog (default for create/edit flows).
     * Use {@link openCenterDialog} for centered popups (confirmations, askemo create/settings, etc.).
     */
    public open<T, D = any, R = any>(component: ComponentType<T>, config?: ExtendedMatDialogConfig<D>): MatDialogRef<T, R> {
        return this.openInternal(component, this.applyRightSideDefaults(config));
    }

    /** Opens a centered dialog (confirmations, askemo create/settings/questions/invitations, chat, etc.). */
    public openCenterDialog<T, D = any, R = any>(component: ComponentType<T>, config?: ExtendedMatDialogConfig<D>): MatDialogRef<T, R> {
        return this.openInternal(component, config);
    }

    public async openErrorDialog(data: { activityId: any; statusCode: number }) {
        const component = await import('@shared/components/dialogs/error-dialog/error-dialog.component').then(mod => mod.ErrorDialogComponent);
        return this.openCenterDialog(component, { data });
    }

    public async openFailureDialog<T = unknown>(error: Error<T>) {
        const component = await import('@shared/components/dialogs/failure-dialog/failure-dialog.component').then(mod => mod.FailureDialogComponent);
        this.openCenterDialog(component, { data: error });
    }

    public async openUnsavedChangesDialog() {
        const component = await import('@shared/components/dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component').then(mod => mod.UnsavedChangesDialogComponent);
        return firstValueFrom(outputToObservable(this.openCenterDialog(component, { disableClose: true }).componentInstance.isDiscarded));
    }

    public async openAutosaveInProgressDialog() {
        const component = await import('@shared/components/dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component').then(mod => mod.UnsavedChangesDialogComponent);
        return firstValueFrom(
            outputToObservable(this.openCenterDialog(component, { disableClose: true, data: { variant: 'autosaveSaving' as const } }).componentInstance.isDiscarded)
        );
    }

    public async openDefaultConfirmationDialog(matConfig?: MatDialogConfig) {
        const component = await import('@shared/components/dialogs/confirmation-dialog/confirmation-dialog.component').then(mod => mod.ConfirmationDialogComponent);
        return this.openCenterDialog(component, matConfig);
    }

    public openConfirmationDialog(config: ConfirmationDialogConfig, matConfig?: MatDialogConfig) {
        const tempConfig = {
            ...defaultConfig,
            ...config,
        };

        return from(this.openDefaultConfirmationDialog(matConfig)).pipe(
            take(1),
            tap(dialog => {
                dialog.componentInstance.confirmationDialogMessageKeyword = tempConfig.primaryMessageKeyword;
                dialog.componentInstance.confirmationDialogHeaderMessageKeyword = tempConfig.headerMessageKeyword;
                dialog.componentInstance.confirmationDialogPrimaryButtonKeyword = tempConfig.primaryButtonKeyword;
                dialog.componentInstance.messageTemplateRef = tempConfig.primaryMessageTemplate;
                dialog.componentInstance.additionalMessage = tempConfig.additionalMessage;
                dialog.componentInstance.confirmationDialogSecondaryMessageKeyword = tempConfig.secondaryMessageKeyword;
                dialog.componentInstance.confirmationDialogDynamicTranslations = tempConfig.dialogDynamicTranslations;
                dialog.componentInstance.confirmationDialogMessageRows = tempConfig.primaryMessageRows;
                dialog.componentInstance.textAreaHeaderKeyword = tempConfig.textAreaHeaderKeyword;
                dialog.componentInstance.dialogTextData = tempConfig.dialogTextData;
                dialog.componentInstance.showCancelButton = tempConfig.showCancelButton;
                dialog.componentInstance.cancelButtonMessage = tempConfig.cancelButtonMessage;
                dialog.componentInstance.showAdditionalActionButton = tempConfig.showAdditionalActionButton;
                dialog.componentInstance.additionalActionButtonKeyword = tempConfig.additionalActionButtonKeyword;
                dialog.componentInstance.mainModalColor = tempConfig.mainModalColor;
                dialog.componentInstance.confirmTypePhraseKeyword = tempConfig.confirmTypePhraseKeyword;
                dialog.componentInstance.icon = tempConfig.icon;
            })
        );
    }

    public async openDefaultImageUploadDialog() {
        const component = await import('@shared/components/dialogs/image-upload-dialog/image-upload-dialog.component').then(mod => mod.ImageUploadDialogComponent);
        return this.openCenterDialog(component);
    }

    public openImageUploadDialog(squareImage: boolean, scopeId: string, file?: File | FileList) {
        return from(this.openDefaultImageUploadDialog()).pipe(
            take(1),
            tap(dialog => {
                dialog.componentInstance.squareImage = squareImage;
                dialog.componentInstance.scopeId = scopeId;
                dialog.componentInstance.file = file;
            })
        );
    }

    public closeAllModals() {
        this.matDialogService.closeAll();
    }

    public openChat(data: ChatDialogData) {
        return import('@pages/chat-assistant/chat-dialog/chat-dialog.component')
            .then(m => m.ChatDialogComponent)
            .then(component => {
                return this.openCenterDialog(component, {
                    width: '100vw',
                    height: '100dvh',
                    minWidth: '100vw',
                    data,
                });
            });
    }

    private openInternal<T, D = any, R = any>(component: ComponentType<T>, config?: ExtendedMatDialogConfig<D>): MatDialogRef<T, R> {
        const disableClose = config?.disableClose ?? false;

        const animation = config?.animation;
        let dialogConfig: MatDialogConfig<D> = {
            ...config,
            disableClose: true,
            autoFocus: false,
        };

        if (animation?.direction && animation.direction !== DialogEntryDirection.None) {
            const panelClass = dialogConfig.panelClass || [];
            const classes = Array.isArray(panelClass) ? [...panelClass] : [panelClass];

            classes.push('animated-dialog');
            classes.push(`slide-from-${animation.direction}`);

            dialogConfig.panelClass = [...new Set(classes.filter(Boolean))];
            dialogConfig.enterAnimationDuration = 0;
            dialogConfig.exitAnimationDuration = 300;
        }

        let backDropClickSub: Subscription;
        let ref = this.matDialogService.open(component, dialogConfig);
        const componentInstance: DialogBase<T> = ref.componentInstance as any;

        if (disableClose === false) {
            if (componentInstance.canCloseDialog) {
                backDropClickSub = ref
                    .backdropClick()
                    .pipe(
                        concatMap(async () => componentInstance.canCloseDialog()),
                        takeUntil(ref.afterClosed()),
                        filter(r => !!r)
                    )
                    .subscribe(() => {
                        ref.close();
                    });
            } else {
                backDropClickSub = ref
                    .backdropClick()
                    .pipe(takeUntil(ref.afterClosed()))
                    .subscribe(() => {
                        ref.close();
                    });
            }
        }

        if (animation?.direction && animation.direction !== DialogEntryDirection.None) {
            // Clear residual identity transform after slide-in (see dialog-animations.scss).
            ref.afterOpened()
                .pipe(take(1), delay(300), takeUntil(ref.beforeClosed()))
                .subscribe(() => {
                    ref.addPanelClass('animation-done');
                });

            ref.beforeClosed()
                .pipe(
                    tap(() => {
                        ref.removePanelClass('animation-done');
                        ref.addPanelClass('closing');
                    })
                )
                .subscribe();
        }

        ref.afterClosed()
            .pipe(take(1))
            .subscribe(() => {
                ref = null;

                if (backDropClickSub?.unsubscribe) {
                    backDropClickSub.unsubscribe();
                }
            });

        return ref;
    }

    private applyRightSideDefaults<D>(config?: ExtendedMatDialogConfig<D>): ExtendedMatDialogConfig<D> {
        const existingPanelClass = config?.panelClass;
        const panelClasses = ['right-side-dialog-panel', ...(Array.isArray(existingPanelClass) ? existingPanelClass : existingPanelClass ? [existingPanelClass] : [])];

        return {
            ...config,
            position: config?.position ?? { right: '0%' },
            width: config?.width ?? 'min(500px, 100vw)',
            maxWidth: config?.maxWidth ?? '100vw',
            height: config?.height ?? '100dvh',
            maxHeight: config?.maxHeight ?? '100dvh',
            panelClass: [...new Set(panelClasses.filter(Boolean))],
            animation: config?.animation ?? { direction: DialogEntryDirection.Right },
        };
    }
}
